import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import OptimizedImage from './OptimizedImage';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Small featured products grid shown inside the HomePopup
 */
const PopupFeaturedProducts = ({ onClose }) => {
  const [products, setProducts] = useState([]);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [settingsRes, productsRes] = await Promise.all([
          axios.get(`${API_URL}/api/site-settings/`).catch(() => ({ data: {} })),
          axios.get(`${API_URL}/api/products/`, { params: { featured: true } }),
        ]);

        const s = settingsRes.data || {};
        if (s.featured_popup_enabled === false) {
          setEnabled(false);
          return;
        }

        const count = s.featured_popup_count || 3;
        const seen = new Set();
        const unique = (productsRes.data || []).filter(p => {
          const key = p.name?.replace(/\s*\(Kopie\)\s*/g, '').trim();
          if (seen.has(key)) return false;
          seen.add(key);
          return p.in_stock !== false;
        });
        setProducts(unique.slice(0, count));
      } catch { /* silent */ }
    };
    fetch();
  }, []);

  if (!enabled || products.length === 0) return null;

  return (
    <div className="px-6 pb-5 pt-2">
      <p className="font-sweet-sans text-[10px] text-earth-brown/60 uppercase tracking-widest mb-2">Uitgelicht</p>
      <div className="grid grid-cols-3 gap-2">
        {products.map((p) => (
          <Link
            key={p.id}
            to={`/product/${p.id}`}
            onClick={onClose}
            className="group block"
          >
            <div className="aspect-square overflow-hidden bg-gray-50 mb-1">
              <OptimizedImage
                src={p.image}
                cloudinaryUrl={p.cloudinary_image_url}
                alt={p.name}
                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <p className="font-sweet-sans text-[9px] text-earth-brown uppercase tracking-wide line-clamp-1">{p.name}</p>
            <p className="font-sweet-sans text-[10px] font-semibold text-terra-beige">€{(p.price || 0).toFixed(2)}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

/**
 * HomePopup - Shows a promotional popup on the home page only.
 * 
 * Features:
 * - ONLY shows on home page (/)
 * - ALWAYS shows on every visit/refresh (no localStorage persistence)
 * - Supports: title, body text, CTA button, media (image/carousel/video)
 * - Closable via X, ESC, click outside (but reappears on next visit)
 * - Matches L-ART brand style
 */
const HomePopup = ({ isHomePage = false }) => {
  const [popup, setPopup] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [hasChecked, setHasChecked] = useState(false);
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Fetch popup on mount (only if on home page)
  useEffect(() => {
    // Only fetch on home page and only once per mount
    if (!isHomePage || hasChecked) return;

    const fetchPopup = async () => {
      try {
        const response = await fetch(`${API_URL}/api/cms/home-popup`);
        if (response.ok) {
          const data = await response.json();
          if (data.popup) {
            setPopup(data.popup);
            // Small delay before showing for better UX
            setTimeout(() => setIsVisible(true), 500);
          }
        }
      } catch (error) {
        console.error('Error fetching home popup:', error);
      }
      setHasChecked(true);
    };

    fetchPopup();
  }, [isHomePage, hasChecked]);

  // Handle close - NO localStorage persistence, just hide for current view
  const handleClose = useCallback(() => {
    setIsVisible(false);
    // Restore focus
    if (previousFocusRef.current) {
      previousFocusRef.current.focus();
    }
  }, []);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isVisible) {
        handleClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      // Save current focus and focus modal
      previousFocusRef.current = document.activeElement;
      modalRef.current?.focus();
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible, handleClose]);

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Carousel navigation
  const nextSlide = () => {
    if (popup?.media_items?.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % popup.media_items.length);
    }
  };

  const prevSlide = () => {
    if (popup?.media_items?.length > 0) {
      setCurrentSlide((prev) => 
        prev === 0 ? popup.media_items.length - 1 : prev - 1
      );
    }
  };

  // Parse video URL (YouTube/Vimeo embed)
  const getVideoEmbedUrl = (url) => {
    if (!url) return null;
    
    // YouTube
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (ytMatch) {
      return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=0&rel=0`;
    }
    
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    
    // Direct MP4
    if (url.endsWith('.mp4') || url.includes('.mp4')) {
      return url;
    }
    
    return null;
  };

  // Don't render if not visible or no popup
  if (!isVisible || !popup) return null;

  const videoEmbedUrl = popup.media_type === 'video' ? getVideoEmbedUrl(popup.video_url) : null;
  const isDirectVideo = videoEmbedUrl && (videoEmbedUrl.endsWith('.mp4') || videoEmbedUrl.includes('.mp4'));

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-almost-black/60 backdrop-blur-sm"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="popup-title"
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className="relative w-full max-w-lg bg-linen-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300"
        style={{ maxHeight: '90vh', borderRadius: 0 }}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 p-2 bg-white/80 hover:bg-white text-earth-brown hover:text-almost-black transition-colors"
          aria-label="Popup sluiten"
          data-testid="popup-close-btn"
          style={{ borderRadius: 0 }}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content Container */}
        <div className="overflow-y-auto" style={{ maxHeight: '85vh' }}>
          {/* Header */}
          <div className="px-6 pt-6 pb-4">
            <h2
              id="popup-title"
              className="font-cormorant text-2xl md:text-3xl text-earth-brown pr-8"
            >
              {popup.title}
            </h2>
            
            {popup.body_text && (
              <div 
                className="mt-3 font-sweet-sans text-sm md:text-base text-almost-black/80 leading-relaxed whitespace-pre-line"
              >
                {popup.body_text}
              </div>
            )}
          </div>

          {/* Media Section */}
          {popup.media_type !== 'none' && (
            <div className="px-6 pb-4">
              {/* Single Image */}
              {popup.media_type === 'image' && popup.media_items?.[0] && (
                <div className="overflow-hidden">
                  <img
                    src={popup.media_items[0].url}
                    alt={popup.media_items[0].alt || popup.title}
                    className="w-full h-auto object-cover"
                  />
                  {popup.media_items[0].caption && (
                    <p className="mt-2 text-xs text-almost-black/60 text-center">
                      {popup.media_items[0].caption}
                    </p>
                  )}
                </div>
              )}

              {/* Carousel */}
              {popup.media_type === 'carousel' && popup.media_items?.length > 0 && (
                <div className="relative">
                  <div className="overflow-hidden">
                    <img
                      src={popup.media_items[currentSlide].url}
                      alt={popup.media_items[currentSlide].alt || `Slide ${currentSlide + 1}`}
                      className="w-full h-auto object-cover"
                    />
                    {popup.media_items[currentSlide].caption && (
                      <p className="mt-2 text-xs text-almost-black/60 text-center">
                        {popup.media_items[currentSlide].caption}
                      </p>
                    )}
                  </div>
                  
                  {/* Carousel Navigation */}
                  {popup.media_items.length > 1 && (
                    <>
                      <button
                        onClick={prevSlide}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white text-earth-brown shadow-md transition-colors"
                        aria-label="Vorige afbeelding"
                        style={{ borderRadius: 0 }}
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={nextSlide}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white text-earth-brown shadow-md transition-colors"
                        aria-label="Volgende afbeelding"
                        style={{ borderRadius: 0 }}
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      
                      {/* Dots */}
                      <div className="flex justify-center gap-2 mt-3">
                        {popup.media_items.map((_, idx) => (
                          <button
                            key={`dot-${idx}`}
                            onClick={() => setCurrentSlide(idx)}
                            className={`w-2 h-2 transition-colors ${
                              idx === currentSlide ? 'bg-earth-brown' : 'bg-earth-brown/30'
                            }`}
                            aria-label={`Ga naar slide ${idx + 1}`}
                            style={{ borderRadius: 0 }}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Video */}
              {popup.media_type === 'video' && videoEmbedUrl && (
                <div className="overflow-hidden aspect-video">
                  {isDirectVideo ? (
                    <video
                      src={videoEmbedUrl}
                      controls
                      className="w-full h-full object-cover"
                      poster=""
                    >
                      Je browser ondersteunt geen video.
                    </video>
                  ) : (
                    <iframe
                      src={videoEmbedUrl}
                      title={popup.title}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {/* CTA Button */}
          {popup.cta_label && popup.cta_url && (
            <div className="px-6 pb-4">
              <a
                href={popup.cta_url}
                onClick={handleClose}
                className="block w-full py-3 px-6 bg-earth-brown hover:bg-earth-brown/90 text-white text-center font-sweet-sans text-sm uppercase tracking-wider transition-colors"
                data-testid="popup-cta-btn"
                style={{ borderRadius: 0 }}
              >
                {popup.cta_label}
              </a>
            </div>
          )}

          {/* Featured Products in Popup */}
          <PopupFeaturedProducts onClose={handleClose} />
        </div>
      </div>
    </div>
  );
};

export default HomePopup;
